import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// Branch delete
export const deleteBranch = createAsyncThunk(
  "branches/deleteBranch",
  async (id, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/branchs/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Branch could not be deleted"
      );
    }
  }
);

// Branch update
export const updateBranch = createAsyncThunk(
  "branches/updateBranch",
  async ({ id, name }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${API_URL}/branchs/${id}`, { name }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Branch could not be updated"
      );
    }
  }
);

// Branch create
export const createBranch = createAsyncThunk(
  "branches/createBranch",
  async (branchData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API_URL}/branchs`, branchData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Branch could not be created"
      );
    }
  }
);

// Get all branches
export const getBranches = createAsyncThunk(
  "branches/getBranches",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/branchs`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "An error occurred while loading branches"
      );
    }
  }
);

// Get a single branch
export const getBranch = createAsyncThunk(
  "branches/getBranch",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/branchs/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Branch information could not be retrieved"
      );
    }
  }
);

const branchSlice = createSlice({
  name: "branches",
  initialState: { 
    branches: [], 
    branch: null,
    loading: false, 
    error: null 
  },
  reducers: {
    clearBranchError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get all branches
      .addCase(getBranches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBranches.fulfilled, (state, action) => {
        state.loading = false;
        state.branches = action.payload;
      })
      .addCase(getBranches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Get single branch
      .addCase(getBranch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBranch.fulfilled, (state, action) => {
        state.loading = false;
        state.branch = action.payload;
      })
      .addCase(getBranch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create branch
      .addCase(createBranch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBranch.fulfilled, (state, action) => {
        state.loading = false;
        state.branches.push(action.payload);
      })
      .addCase(createBranch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update branch
      .addCase(updateBranch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBranch.fulfilled, (state, action) => {
        state.loading = false;
        state.branches = state.branches.map(branch =>
          branch._id === action.payload._id ? action.payload : branch
        );
      })
      .addCase(updateBranch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete branch
      .addCase(deleteBranch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBranch.fulfilled, (state, action) => {
        state.loading = false;
        state.branches = state.branches.filter(branch => branch._id !== action.payload);
      })
      .addCase(deleteBranch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearBranchError } = branchSlice.actions;
export default branchSlice.reducer;